"use client";

import { useUserInfo } from "@/api/user/use-query";
import { TUserInfo } from "@/typings";
import React, { useEffect, useMemo, useState } from "react";
import { Message } from "@arco-design/web-react";
import { usePathname, useRouter } from "next/navigation";
import { clientWindow } from "@/utils/dom";
import { isMobile } from "@/utils/is";
import { initializeApp } from "firebase/app";
import { getAnalytics } from "firebase/analytics";
import { NEXT_PUBLIC_REACT_APP_DOMAIN } from "@/common";
import { useTrackingRecordSave } from "@/api/track/use-query";
import { TrackingEventID, TrackingEventType } from "@/shared/enums";
import { base64Decode, getUriQuery } from "@/utils/url";
import { RouteStack } from "@/shared";
import LoginModal from "./(routeLayout)/login/(components)/LoginModal";

const firebaseConfig = {
  apiKey: "AIzaSyBLg_W5LklufLsoNIzb15Kth7LygPEnQ_4",
  authDomain: "yolobuyer-test-e92c1.firebaseapp.com",
  projectId: "yolobuyer-test-e92c1",
  storageBucket: "yolobuyer-test-e92c1.firebasestorage.app",
  messagingSenderId: "103136175365",
  appId: "1:103136175365:web:23e18fa13cbceda740836c",
  measurementId: "G-3KDEPYCYGF",
};

export interface LoginContextProps {
  userInfo?: TUserInfo;
  $userInfo?: (userInfo: TUserInfo) => void;
  $isUserLoggedIn?: (isUserLoggedIn: boolean) => void;
  $loginVisible?: (v: boolean) => void;
}

const LoginContext = React.createContext<LoginContextProps>({});

const allowlist = [`/${RouteStack.PersonalCenter}`];

function LoginProvider({ children }: { children: React.ReactNode }) {
  const path = usePathname();
  const router = useRouter();

  const [prevPath, $PrevPath] = useState("");
  const [isFetchUserInfo, $isFetchUserInfo] = useState(false);

  const { mutate: fetchUserInfo } = useUserInfo();

  const [isUserLoggedIn, $isUserLoggedIn] = useState<boolean>();

  const [userData, $userData] = useState<TUserInfo>();

  const [loginVisible, $loginVisible] = useState<boolean>(false);

  const isMatchSampleUrl = useMemo(
    () => /^\/product\/detail\/[^/]+\/sample$/.test(path),
    [path]
  );

  const { mutate: fetchTrackingRecordSave } = useTrackingRecordSave();

  useEffect(() => {
    if (NEXT_PUBLIC_REACT_APP_DOMAIN === "online") {
      // Initialize Firebase
      const app = initializeApp(firebaseConfig);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const analytics = getAnalytics(app);
    }
  }, []);

  useEffect(() => {
    if (path !== prevPath && isFetchUserInfo) {
      fetchTrackingRecordSave({
        uid: userData?.uid,
        event_id: TrackingEventID.PageLoad,
        event_type: TrackingEventType.Exposure,
      });
      $PrevPath(path);
    }
  }, [path, prevPath, fetchTrackingRecordSave, userData, isFetchUserInfo]);

  useEffect(() => {
    if (clientWindow) {
      if (
        path === `/${RouteStack.Oauth.callback}` ||
        path === `/${RouteStack.Oauth.ttspc}`
      ) {
        if (isMobile()) {
          const query = clientWindow?.location.search;

          fetchTrackingRecordSave({
            event_id: TrackingEventID.MobileEntersPCShowcase,
            event_type: TrackingEventType.Exposure,
            ext: {
              showcase_oauth_callback: getUriQuery(query),
            },
          });

          const path = clientWindow?.location.pathname;
          const newPath =
            path === `/${RouteStack.Oauth.callback}`
              ? `/${RouteStack.Oauth.callback}/`
              : path;
          const toPath = "yolobuyer://yolobuyer" + newPath + query;
          location.href = toPath;
        }
      }
    }
  }, [fetchTrackingRecordSave, path]);

  useEffect(() => {
    if (userData) {
      if (
        path === `/${RouteStack.Login}` ||
        path === `/${RouteStack.Oauth.callback}`
      ) {
        const query = getUriQuery(clientWindow?.location.search || "", [
          "redirect_url",
        ]);
        if (query?.redirect_url) {
          try {
            router.push(base64Decode(query?.redirect_url));
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
          } catch (error) {
            router.push(`/${RouteStack.Home}`);
          }
        } else {
          router.push(`/${RouteStack.Home}`);
        }
      }
    }
  }, [userData, path, router]);

  useEffect(() => {
    fetchUserInfo(
      {},
      {
        onSuccess: (res) => {
          if (res?.data) {
            $userData(res.data);
          }
        },
        onError: () => {
          $userData(undefined);
          if (allowlist.includes(path) || isMatchSampleUrl) {
            Message.info("You are currently not logged in");
            router.push(`/${RouteStack.Home}`);
          }
        },
        onSettled: () => {
          $isFetchUserInfo(true);
        },
      }
    );
  }, [fetchUserInfo, isUserLoggedIn, path, isMatchSampleUrl, router]);

  return (
    <LoginContext.Provider
      value={{ userInfo: userData, $isUserLoggedIn, $loginVisible }}
    >
      {loginVisible && (
        <LoginModal
          visible={loginVisible}
          onCancel={() => $loginVisible(false)}
        />
      )}
      {children}
    </LoginContext.Provider>
  );
}

export { LoginContext, LoginProvider };
